import { useState, FormEvent } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  getTasksByWorkspace,
  createTask,
  updateTaskStatus,
  updateTaskPriority,
  deleteTask,
  assignTask,
} from '../lib/services/tasks.service';
import { getTeamMembersByWorkspace, getTeamMemberById } from '../lib/services/team.service';
import {
  PlusIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  CalendarIcon,
  UserIcon,
  FlagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import clsx from 'clsx';
import {
  TASK_STATUS_CONFIG,
  TASK_PRIORITY_CONFIG,
  type Task,
  type TaskStatus,
  type TaskPriority,
} from '../lib/types';

const COLUMNS: TaskStatus[] = ['triage', 'backlog', 'in_progress', 'in_review', 'done'];

export function TasksPage() {
  const { currentWorkspace, refreshWorkspaces } = useWorkspace();
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);

  if (!currentWorkspace) return null;

  const tasks = getTasksByWorkspace(currentWorkspace.id);
  const teamMembers = getTeamMembersByWorkspace(currentWorkspace.id);

  const tasksByStatus = COLUMNS.reduce((acc, status) => {
    acc[status] = tasks.filter((t) => t.status === status);
    return acc;
  }, {} as Record<TaskStatus, Task[]>);

  const handleMoveTask = (taskId: string, newStatus: TaskStatus) => {
    updateTaskStatus(taskId, newStatus);
    refreshWorkspaces();
  };

  const handleCreateTask = (data: { title: string; description: string; priority: TaskPriority; assigneeId?: string }) => {
    createTask({
      workspaceId: currentWorkspace.id,
      ...data,
      status: 'triage',
    });
    setShowAddForm(false);
    refreshWorkspaces();
  };

  const onDragStart = (e: React.DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.setData('taskId', taskId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e: React.DragEvent, newStatus: TaskStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId') || draggedTaskId;
    if (taskId) {
      handleMoveTask(taskId, newStatus);
    }
    setDraggedTaskId(null);
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white shadow-sm">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-gray-900 tracking-tight">Issues</h1>
          <div className="flex items-center gap-2 text-[13px] font-medium text-gray-500">
            <span className="bg-gray-100 px-2 py-0.5 rounded-full">{tasks.length} total</span>
            <span>·</span>
            <span className="text-primary-600">{tasks.filter((t) => t.status !== 'done' && t.status !== 'cancelled').length} active</span>
          </div>
        </div>
        <button onClick={() => setShowAddForm(true)} className="btn-primary btn-sm rounded-lg">
          <PlusIcon className="h-4 w-4" />
          New Issue
        </button>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto p-6 bg-gray-50/30">
        <div className="flex gap-4 min-w-max h-full">
          {COLUMNS.map((status) => {
            const config = TASK_STATUS_CONFIG[status];
            const columnTasks = tasksByStatus[status];

            return (
              <div
                key={status}
                className="w-80 flex flex-col"
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, status)}
              >
                {/* Column header */}
                <div className="pipeline-header border-b-2 border-transparent px-2 py-3">
                  <div className="pipeline-header-title font-semibold text-gray-700">
                    <span className={`status-dot status-dot-${config.color} ring-4 ring-${config.color}-50`} />
                    {config.label}
                  </div>
                  <span className="pipeline-header-count bg-white shadow-sm ring-1 ring-gray-200">{columnTasks.length}</span>
                </div>

                {/* Column content */}
                <div className={clsx(
                  'pipeline-column flex-1 overflow-y-auto mt-2 space-y-2.5 transition-colors duration-200 rounded-xl',
                  draggedTaskId && 'bg-gray-100/50 ring-2 ring-inset ring-gray-200/50 ring-dashed'
                )}>
                  {columnTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      isDragging={draggedTaskId === task.id}
                      onDragStart={(e) => onDragStart(e, task.id)}
                      onSelect={() => setSelectedTask(task)}
                      onDelete={() => {
                        if (confirm(`Delete task "${task.title}"?`)) {
                          deleteTask(task.id);
                          refreshWorkspaces();
                        }
                      }}
                      onMoveForward={status !== 'done' ? () => {
                        const nextIndex = COLUMNS.indexOf(status) + 1;
                        if (nextIndex < COLUMNS.length) {
                          handleMoveTask(task.id, COLUMNS[nextIndex]);
                        }
                      } : undefined}
                      onMoveBackward={status !== 'triage' ? () => {
                        const prevIndex = COLUMNS.indexOf(status) - 1;
                        if (prevIndex >= 0) {
                          handleMoveTask(task.id, COLUMNS[prevIndex]);
                        }
                      } : undefined}
                    />
                  ))}
                  {columnTasks.length === 0 && (
                    <div className="text-center py-12 text-sm text-gray-400 font-medium italic">
                      No issues
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add Task Modal */}
      {showAddForm && (
        <AddTaskModal
          teamMembers={teamMembers}
          onClose={() => setShowAddForm(false)}
          onSubmit={handleCreateTask}
        />
      )}

      {/* Task Detail Sidebar */}
      {selectedTask && (
        <TaskDetailSidebar
          task={selectedTask}
          teamMembers={teamMembers}
          onClose={() => setSelectedTask(null)}
          onUpdate={refreshWorkspaces}
        />
      )}
    </div>
  );
}

function TaskCard({
  task,
  isDragging,
  onDragStart,
  onSelect,
  onDelete,
  onMoveForward,
  onMoveBackward,
}: {
  task: Task;
  isDragging?: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onSelect: () => void;
  onDelete: () => void;
  onMoveForward?: () => void;
  onMoveBackward?: () => void;
}) {
  const priorityConfig = TASK_PRIORITY_CONFIG[task.priority];
  const assignee = task.assigneeId ? getTeamMemberById(task.assigneeId) : null;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={clsx(
        'pipeline-item group cursor-grab active:cursor-grabbing transition-all duration-200 border-gray-200 hover:border-primary-300 hover:shadow-md',
        isDragging && 'opacity-40 scale-95 ring-2 ring-primary-400 border-primary-400'
      )}
      onClick={onSelect}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px] font-mono font-medium text-gray-400 mb-1.5 uppercase tracking-wider">
            <span>{task.identifier}</span>
            {task.estimate && <span className="bg-gray-50 px-1.5 rounded">· {task.estimate}h</span>}
          </div>
          <h3 className="text-[13.5px] font-semibold text-gray-800 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
            {task.title}
          </h3>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="p-1.5 rounded-md hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
          title="Delete"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      </div>

      {task.description && (
        <p className="text-xs text-gray-500 mt-2.5 line-clamp-2 leading-relaxed">{task.description}</p>
      )}

      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-3">
          {task.priority !== 'no_priority' && (
            <span className={clsx(
              'inline-flex items-center px-1.5 py-0.5 rounded ring-1 ring-inset',
              priorityConfig.color === 'red' ? 'text-red-600 bg-red-50 ring-red-100' :
              priorityConfig.color === 'orange' ? 'text-orange-600 bg-orange-50 ring-orange-100' :
              priorityConfig.color === 'blue' ? 'text-blue-600 bg-blue-50 ring-blue-100' : 'text-gray-500 bg-gray-50 ring-gray-100'
            )}>
              <FlagIcon className="h-3 w-3 mr-1" />
              <span className="text-[10px] font-bold uppercase tracking-tighter">{priorityConfig.label}</span>
            </span>
          )}
          {task.dueDate && (
            <span className="flex items-center gap-1 text-[11px] font-medium text-gray-500">
              <CalendarIcon className="h-3 w-3" />
              {format(new Date(task.dueDate), 'MMM d')}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          {/* Move buttons - kept for accessibility/mobile */}
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {onMoveBackward && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveBackward(); }}
                className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                title="Move back"
              >
                <ChevronLeftIcon className="h-3.5 w-3.5" />
              </button>
            )}
            {onMoveForward && (
              <button
                onClick={(e) => { e.stopPropagation(); onMoveForward(); }}
                className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600"
                title="Move forward"
              >
                <ChevronRightIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Assignee */}
          {assignee ? (
            <div className="avatar avatar-xs ring-2 ring-white" title={`Assigned to: ${assignee.name}`}>
              {assignee.name.charAt(0)}
            </div>
          ) : (
            <div className="avatar avatar-xs bg-gray-50 text-gray-300 ring-2 ring-white">
              <UserIcon className="h-3 w-3" />
            </div>
          )}
        </div>
      </div>

      {/* Tags */}
      {task.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-gray-50">
          {task.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] px-2 py-0.5 bg-gray-50 text-gray-500 rounded-md border border-gray-100 font-medium">
              {tag}
            </span>
          ))}
          {task.tags.length > 3 && (
            <span className="text-[10px] text-gray-400 ml-1">+{task.tags.length - 3}</span>
          )}
        </div>
      )}
    </div>
  );
}

function AddTaskModal({
  teamMembers,
  onClose,
  onSubmit,
}: {
  teamMembers: ReturnType<typeof getTeamMembersByWorkspace>;
  onClose: () => void;
  onSubmit: (data: { title: string; description: string; priority: TaskPriority; assigneeId?: string }) => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('no_priority');
  const [assigneeId, setAssigneeId] = useState<string>('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit({
      title: title.trim(),
      description: description.trim(),
      priority,
      assigneeId: assigneeId || undefined,
    });
  };

  return (
    <div className="modal">
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content animate-slide-in">
        <div className="modal-header">
          <h2 className="text-lg font-semibold text-gray-900">Create New Task</h2>
          <button onClick={onClose} className="btn-icon">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-4">
            <div>
              <label className="label">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input"
                placeholder="What needs to be done?"
                autoFocus
              />
            </div>
            <div>
              <label className="label">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="textarea"
                placeholder="Add more details..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as TaskPriority)}
                  className="select"
                >
                  {Object.entries(TASK_PRIORITY_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Assignee</label>
                <select
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                  className="select"
                >
                  <option value="">Unassigned</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>{member.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={!title.trim()}>
              Create Task
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TaskDetailSidebar({
  task,
  teamMembers,
  onClose,
  onUpdate,
}: {
  task: Task;
  teamMembers: ReturnType<typeof getTeamMembersByWorkspace>;
  onClose: () => void;
  onUpdate: () => void;
}) {
  const handleStatusChange = (newStatus: TaskStatus) => {
    updateTaskStatus(task.id, newStatus);
    onUpdate();
  };

  const handlePriorityChange = (newPriority: TaskPriority) => {
    updateTaskPriority(task.id, newPriority);
    onUpdate();
  };

  const handleAssigneeChange = (newAssigneeId: string) => {
    assignTask(task.id, newAssigneeId || undefined);
    onUpdate();
  };

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white border-l border-gray-200 shadow-lg z-50 animate-slide-in-right">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <span className="text-sm font-mono text-gray-500">{task.identifier}</span>
        <button onClick={onClose} className="btn-icon">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-4rem)]">
        <h2 className="text-lg font-semibold text-gray-900">{task.title}</h2>

        {task.description && (
          <p className="text-sm text-gray-600">{task.description}</p>
        )}

        <div className="space-y-4">
          <div>
            <label className="label">Status</label>
            <select
              value={task.status}
              onChange={(e) => handleStatusChange(e.target.value as TaskStatus)}
              className="select"
            >
              {COLUMNS.map((status) => (
                <option key={status} value={status}>{TASK_STATUS_CONFIG[status].label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Priority</label>
            <select
              value={task.priority}
              onChange={(e) => handlePriorityChange(e.target.value as TaskPriority)}
              className="select"
            >
              {Object.entries(TASK_PRIORITY_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Assignee</label>
            <select
              value={task.assigneeId || ''}
              onChange={(e) => handleAssigneeChange(e.target.value)}
              className="select"
            >
              <option value="">Unassigned</option>
              {teamMembers.map((member) => (
                <option key={member.id} value={member.id}>{member.name}</option>
              ))}
            </select>
          </div>

          {task.dueDate && (
            <div>
              <label className="label">Due Date</label>
              <p className="text-sm text-gray-900">{format(new Date(task.dueDate), 'MMMM d, yyyy')}</p>
            </div>
          )}

          {task.estimate && (
            <div>
              <label className="label">Estimate</label>
              <p className="text-sm text-gray-900">{task.estimate} hours</p>
            </div>
          )}

          {task.tags.length > 0 && (
            <div>
              <label className="label">Tags</label>
              <div className="flex flex-wrap gap-1">
                {task.tags.map((tag) => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="pt-4 border-t border-gray-200 text-xs text-gray-400">
          <p>Created {format(new Date(task.createdAt), 'MMM d, yyyy')}</p>
          <p>Updated {format(new Date(task.updatedAt), 'MMM d, yyyy')}</p>
        </div>
      </div>
    </div>
  );
}

export default TasksPage;
